import type React from 'react';
import { useContext, useEffect } from 'react';
import { ChatContainer } from '@trinity/chat-components/components/BrandInsights/Chat.Container.js';
import { ChatViewType, ChatViewTypeContext } from '@trinity/chat-components/components/BrandInsights/context.js';
import { LinkStateContainer } from '@trinity/chat-components/components/BrandInsights/Link.Container.js';
import { ChatStateContainer } from '@trinity/chat-components/components/Chat.State/Chat.State.js';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context.js';
import { QueryDownloaderContainer } from '@trinity/chat-components/containers/QueryDownloader/QueryDownloader.Container.js';
import type { BrandInsightsPageContext, TTSConfig } from '@trinity/chat';
import { ChatPageContextDataContext } from '../Chat/context';
import { DigitalTwinScreen } from './DigitalTwinScreen';

type Props = {
  /**
   * When provided, renders [Sidebar | galleryContent] instead of the full
   * Digital Twin layout. Used for the HCP Digital Twins gallery where the
   * user selects a segment before a chat session is activated.
   */
  galleryContent?: React.ReactNode;
  /**
   * HeyGen avatar ID for the Digital Twin persona.
   * When provided, activates the live avatar session.
   * When omitted, HeyGenPanel shows a placeholder.
   */
  avatarId?: string;
  /**
   * Segment chat page context to inject into an inner ChatPageContextDataContext
   * provider. When provided, the Digital Twin chat reads segment tag filters
   * from this inner context rather than the outer one — which may be null when
   * the floating widget is suppressed on the Digital Twin tab.
   */
  chatPageContext?: BrandInsightsPageContext.Item;
  /**
   * TTS provider configuration for LITE mode avatar lip-sync.
   * When provided, HeyGenResponseBridge will synthesize audio for each AI
   * response and drive the avatar via session.repeatAudio().
   * Pass null to disable TTS (avatar is still shown but does not speak).
   */
  ttsConfig: TTSConfig | null;
  /**
   * When true, disables HeyGen video mode entirely.
   * The chat renders full-width in text mode with the segment avatar
   * shown on AI responses instead of the lightbulb.
   */
  disableVideoMode?: boolean;
  /**
   * Segment icon URL to display on AI responses instead of the lightbulb.
   * Used when SegmentationDashboardContext is not available (e.g. chat app).
   */
  segmentIconUrl?: string;
  /**
   * Initial input value to populate in the chat input field.
   * When this value changes, the chat input will be updated.
   */
  initialInput?: string;
};

/**
 * Inner component that handles the initialInput logic.
 * Must be inside ChatStateContainer to access dispatch.
 */
function DigitalTwinChatInner({ initialInput, ...props }: Props) {
  const dispatch = useContext(ChatStateDispatchContext);

  useEffect(() => {
    if (initialInput && dispatch) {
      dispatch({
        type: 'input-changed',
        payload: { value: initialInput },
      });
    }
  }, [initialInput, dispatch]);

  return <DigitalTwinScreen avatarId={props.avatarId} ttsConfig={props.ttsConfig} galleryContent={props.galleryContent} disableVideoMode={props.disableVideoMode} segmentIconUrl={props.segmentIconUrl} />;
}

/**
 * DigitalTwinChat — inline 3-column chat layout for the Digital Twin tab.
 *
 * Layout: [ChatSidebar] | [HeyGenPanel] | [ChatPanel]
 *
 * The provider stack mirrors ChatWidgetContainer (WidgetContainer.tsx) but:
 * - Uses ChatViewType.Page instead of Modal (no Dialog wrapper)
 * - Is always mounted when the Digital Twin tab is active (no open/close gate)
 * - The HeyGenPanel occupies the center column (60% width, min 600px)
 *
 * When `chatPageContext` is provided, it is re-provided via an inner
 * ChatPageContextDataContext.Provider so the segment tag filters remain
 * available inside the Digital Twin chat even when the outer context is null
 * (i.e. when the floating widget is suppressed).
 *
 * HeyGen LITE mode:
 * When avatarId is provided, LiveAvatarSessionManager fetches a session token
 * from POST /v1/chat/live-avatar/session, then LiveAvatarContextProvider starts
 * the SDK session. LiveAvatar attaches the video stream, and HeyGenResponseBridge
 * detects new AI responses and will call repeatAudio() once the TTS backend
 * endpoint is wired up (see HeyGenResponseBridge.tsx).
 */
export const DigitalTwinChat = (props: Props) => {
  const content = (
    <ChatStateContainer>
      <LinkStateContainer>
        <ChatViewTypeContext.Provider value={ChatViewType.Page}>
          <QueryDownloaderContainer>
            <ChatContainer>
              <DigitalTwinChatInner {...props} />
            </ChatContainer>
          </QueryDownloaderContainer>
        </ChatViewTypeContext.Provider>
      </LinkStateContainer>
    </ChatStateContainer>
  );

  if (props.chatPageContext !== undefined) {
    return (
      <ChatPageContextDataContext.Provider value={props.chatPageContext}>
        {content}
      </ChatPageContextDataContext.Provider>
    );
  }

  return content;
};

